/**
 * Foto protegida do médico: carregada como `ImageBitmap` e desenhada num `<canvas>` cujo
 * `toDataURL`/`toBlob` não devolvem a imagem (extraído do `ProtectedAvatar.vue`; também usado
 * pelo avatar da S1).
 */

export async function fetchAsImageBitmap(url: string, signal: AbortSignal): Promise<ImageBitmap> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`${response.status}`)
  const blob = await response.blob()
  return createImageBitmap(blob)
}

export function loadViaImage(url: string): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      createImageBitmap(img).then(resolve, reject)
    }
    img.onerror = reject
    img.src = url
  })
}

/** `fetch` + fallback por `<img>` (URLs legadas do Azure Blob podem não ter CORS). */
export async function loadProtectedBitmap(url: string, signal: AbortSignal): Promise<ImageBitmap> {
  try {
    return await fetchAsImageBitmap(url, signal)
  } catch (error) {
    if (signal.aborted) throw error
    return loadViaImage(url)
  }
}

/** Impede extrair a foto pelo canvas. Chamar depois de cada desenho. */
export function protectCanvas(canvas: HTMLCanvasElement): void {
  canvas.toDataURL = () => ''
  canvas.toBlob = (cb: BlobCallback) => cb(null)
}

/** Desenha o bitmap em círculo ("cover") num canvas de `size × size` px CSS e o protege. */
export function drawCircleCover(
  canvas: HTMLCanvasElement,
  bitmap: ImageBitmap,
  size: number,
  dpr: number
): void {
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.beginPath()
  ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()
  const scale = Math.max(canvas.width / bitmap.width, canvas.height / bitmap.height)
  const w = bitmap.width * scale
  const h = bitmap.height * scale
  ctx.drawImage(bitmap, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h)
  ctx.restore()
  protectCanvas(canvas)
}
